import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import FinalCta from "../sections/FinalCta";
import { IMG, resolveImage } from "../data/content";

const PILLARS = [
  {
    label: "Off the grid",
    text: "No mains electricity for miles. The lodge runs on the sun and the rhythm of the day — lamplight after dusk, and a sky left dark enough to hold every star. No Wi-Fi, no traffic, no distractions.",
  },
  {
    label: "Walked, not driven",
    text: "The last 3.5 kilometres to The Jamoon are walked. No roads are cut, no vehicles brought in. Arriving on foot is not an inconvenience — it is the lightest way to enter a forest.",
  },
  {
    label: "Leave no trace",
    text: "Everything non-biodegradable is carried back out. We build little, take nothing, and leave the ten acres around us exactly as the forest keeps them.",
  },
  {
    label: "Observe, don't intrude",
    text: "We watch through hides and camera traps rather than chasing a sighting. The wild is given its distance — and rewards the patient far more than the pursuing.",
  },
];

const PRACTICES = [
  "Solar power and lamplight in place of a generator's hum.",
  "Rainwater and the river respected; nothing wasted, nothing polluted.",
  "Meals built from what grows in and around the lodge, and from nearby villages.",
  "Local naturalists and staff — the forest's own people, employed and heard.",
  "Single-use plastic kept out of the forest entirely.",
  "Guest numbers kept low by design — five tents, and no more.",
];

export default function ConservationPage() {
  return (
    <main data-testid="conservation-page">
      <Seo
        title="Conservation | The Jamoon, Corbett"
        description="Discover The Jamoon's approach to conservation, biodiversity and responsible wilderness hospitality in the forests and landscapes of Corbett."
        path="/conservation"
        image={IMG.river}
        imageAlt="Elephants beside the Ramganga in the Corbett landscape"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Conservation", path: "/conservation" }]}
      />
      <PageHero
        testid="conservation-hero"
        image={IMG.river}
        imageAlt="Elephants beside the Ramganga in the Corbett landscape"
        eyebrow="Conservation"
        title={<>Conservation at <span className="italic text-brass">The Jamoon</span></>}
        sub="The Jamoon is built to be barely there — off the grid, walked into, and taken apart far more gently than most places are put together."
      />

      {/* Ethos */}
      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-12 sm:py-32 lg:px-20">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal>
              <p className="text-xs font-medium uppercase tracking-[0.3em] text-brass">Our Ethos</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-3xl font-light leading-tight tracking-tight text-ivory sm:text-4xl">
                No Wi-Fi. No traffic. <span className="italic text-brass">No distractions.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-ivory/75">
                We did not want to bring the world to the forest. So we left most of it behind.
                There is no mains power, no network, no road to the door — only the sun, the
                Ramganga, lamplight and the long walk in.
              </p>
              <p className="mt-5 max-w-xl text-sm leading-relaxed text-ivory/55 sm:text-base">
                It is a quieter way to stay, and a far lighter one for the forest. To walk on the
                footsteps of Jim Corbett is to move through this place without leaving much of a
                mark at all.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="group relative overflow-hidden">
            <img
              src={IMG.golGhar}
              alt="The Gol Ghar at The Jamoon, lit only by lamplight and fire after dark"
              loading="lazy"
              className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 lg:aspect-[5/6]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/50 to-transparent" />
            <p className="absolute bottom-5 left-5 text-[11px] tracking-[0.25em] text-ivory/80">
              LAMPLIGHT · NO MAINS POWER
            </p>
          </Reveal>
        </div>

        {/* Pillars */}
        <div className="mt-20 grid gap-8 md:grid-cols-2">
          {PILLARS.map((p, i) => (
            <Reveal key={p.label} delay={i * 0.1}>
              <div data-testid={`conservation-pillar-${i}`} className="h-full border border-brass/20 p-8 sm:p-10">
                <span className="font-serif text-lg italic text-brass/70">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-serif text-2xl font-light text-ivory">{p.label}</h3>
                <p className="mt-4 text-sm leading-relaxed text-ivory/60 sm:text-base">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Statement band */}
      <section className="border-y border-brass/15 bg-forest px-6 py-24 sm:py-28">
        <div className="mx-auto max-w-4xl text-center">
          <Reveal>
            <p className="font-serif text-2xl font-light italic leading-snug text-ivory sm:text-3xl lg:text-4xl">
              "The best way to protect a forest is to need very little from it."
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-6 text-[11px] font-medium tracking-[0.35em] text-brass">THE JAMOON · ON THE RAMGANGA</p>
          </Reveal>
        </div>
      </section>

      {/* Practices */}
      <section className="mx-auto max-w-4xl px-6 py-24 sm:px-12 sm:py-28">
        <Reveal>
          <p className="text-xs font-medium uppercase tracking-[0.3em] text-brass">Low-Impact Living</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif text-3xl font-light tracking-tight text-ivory sm:text-4xl">
            How we keep our <span className="italic text-brass">footprint small.</span>
          </h2>
        </Reveal>
        <div className="mt-10">
          {PRACTICES.map((r, i) => (
            <Reveal key={r} delay={i * 0.05}>
              <p data-testid={`conservation-practice-${i}`} className="border-t border-ivory/10 py-4 text-sm leading-relaxed text-ivory/70 last:border-b sm:text-base">
                <span className="mr-4 font-serif italic text-brass/70">{String(i + 1).padStart(2, "0")}</span>
                {r}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      <FinalCta />
    </main>
  );
}
